import {action, _, autorun, classNames, computed, Fragment, jsondiff, Keys, observable, observer, Popover2, React, useEffect} from '@api'
import {Button, Classes} from '@blueprintjs/core'
import {FormControlLabel, Grid, Switch, TextField} from '@material-ui/core'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {ICatalogReportEs, StoresInstance} from '@stores'
import * as css from './ReportPropertiesPanel.css'
import {runInAction} from 'mobx'

export class ReportPropertiesPanelStore extends VipLocalStore {
  constructor(stores: StoresInstance) {
    super(stores)
  }

  @computed get report() {
    const {reports: {isSaveAsDialogOpen, saveAsDialogReport, currentReport}} = this
    return isSaveAsDialogOpen ? saveAsDialogReport.report : currentReport.report
  }

  registerEffects = () => {
    const toDispose = [
      autorun(() => {
        const {report} = this
        runInAction(() => {
          this.originalReport = _.cloneDeep(report)
          this.draftReport = _.cloneDeep(report)
        })
      })
    ]

    return () => toDispose.forEach(d => d())
  }

  @observable originalReport: Partial<ICatalogReportEs>

  /**
   * Don't modify the real one until they click OK
   */
  @observable draftReport: Partial<ICatalogReportEs>

  @action toggleIsPublic = () => {
    this.draftReport.isPublic = !this.draftReport.isPublic
  }

  @action onPanelKeyPress = (e: React.KeyboardEvent) => {
    if (e.which == Keys.ENTER) {
      this.onOK()
    }
  }
  @action onChangeTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.draftReport.title = e.target.value
  }

  @action onChangeSlug = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.draftReport.slug = e.target.value.replaceAll(' ', '-').toLowerCase()
  }

  @action onCancel = () => {
    _.assign(this.draftReport, _.cloneDeep(this.originalReport))
    this.close()
  }

  @observable isUpdating = false

  @action onApplyChanges = async () => {
    const {stores: {catalog}, draftReport, reports: {isSaveAsDialogOpen, onSaveAs_commit, currentReport}} = this
    this.isUpdating = true
    try {
      await isSaveAsDialogOpen ? onSaveAs_commit(draftReport) : currentReport.updateSettings(catalog.reportSettings, draftReport, true)
    } catch (err) {
      this.catalog.onError(err)
    } finally {
      this.isUpdating = false
    }
  }

  @action onOK = async () => {
    await this.onApplyChanges()
    this.close()
  }

  @action close = () => {
    this.reports.cancelReportDialog()
  }

  popover: Popover2<HTMLDivElement>
  @action setPopoverRef = r => this.popover = r

  @action onDeleteReport = async (e: React.MouseEvent) => {
    const {stores: {currentUser, reports, reports: {isSaveAsDialogOpen, cancelReportDialog, currentReport, recentReports}, catalog: {idFor}}, report} = this, reportId = idFor(report)

    e.stopPropagation()
    reports.recentReports = (recentReports ?? []).filter(id => id !== reportId)

    if (confirm(`Delete this report?`)) {
      this.close()
      if (isSaveAsDialogOpen) {
        await cancelReportDialog()
      }
      else {
        await currentReport.deleteReport()
      }
    }
  }

  get isSaveAs() { return this.reports.isSaveAsDialogOpen}

  @computed get isDirty() {
    const {draftReport, originalReport, isSaveAs} = this

    if (isSaveAs) return true

    const diff = jsondiff.diff(draftReport, originalReport)
    if (diff) {
      return true
    }

    return false
  }

  @computed get isEditable() {
    return this.draftReport.creator === this.stores.reports.reportUserId
  }

  @observable isEditing = false

  get reports() { return this.stores.reports}
}

export const ReportPropertiesPanel = observer(function ReportPropertiesPanel() {
  const store = useVipLocalStore((stores) => new ReportPropertiesPanelStore(stores))

  useEffect(() => store.registerEffects(), [])

  if (!store.draftReport) return null

  const {
    onPanelKeyPress,
    draftReport: {title, isPublic, version, slug},
    isEditable, isSaveAs, toggleIsPublic, onChangeTitle, onChangeSlug, onCancel, onOK
  } = store

  return (<Fragment>
    <div className={classNames(css.reportProperties, Classes.DIALOG_BODY)} onKeyDown={onPanelKeyPress}>
      <Grid container direction='row' spacing={1} className={css.content}>
        <Grid item xs={12}>
          <TextField label='Title' value={title ?? ''} placeholder='Report Title' onChange={onChangeTitle} fullWidth/>
        </Grid>
        {/*<Grid item xs={4}>*/}
        {/*  <TextField label='Version:' fullWidth value={version} disabled/>*/}
        {/*</Grid>*/}
        <Grid item xs={8}>
          <TextField label='Custom URL' fullWidth value={slug ?? ''} placeholder='Custom URL (my-report-name)' onChange={onChangeSlug}/>
        </Grid>
        <Grid item xs={4}>
          <FormControlLabel
            control={
              <Switch
                checked={isPublic === true}
                onChange={toggleIsPublic}
                disabled={!isSaveAs && !isEditable}
                color='secondary'
              />
            }
            label='Public'
          />
        </Grid>

      </Grid>
    </div>
    <div className={classNames(css.actions, Classes.DIALOG_FOOTER)}>
      <div className={Classes.DIALOG_FOOTER_ACTIONS}>
        <Button onClick={onCancel} color='primary'>
          Cancel
        </Button>
        <Button onClick={onOK}
                // disabled={!isDirty}
                color='secondary'>
          {isEditable ? 'OK' : 'Make a Copy'}
        </Button>
      </div>
    </div>
  </Fragment>)
})



// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/ReportPropertiesPanel.tsx